import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  createBillType,
  listBillTypes,
  updateBillTypeStatus,
} from "../erpClient";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { createErpSocket } from "../utils/socketIo";
import { getUserData } from "./const";

const ITEMS_PER_PAGE = 10;

function BillTypeTable() {
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const [billTypes, setBillTypes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBillType, setSelectedBillType] = useState(null);

  // Add Bill Type form states
  const [billTypeName, setBillTypeName] = useState("");
  const [isGstApplicable, setIsGstApplicable] = useState("no");
  const [gstPercentage, setGstPercentage] = useState("");
  const [socket, setSocket] = useState(null);
  const userData = getUserData();

  const fetchBillTypes = async () => {
    setIsLoading(true); // Set loading to true before fetching
    try {
      const response = await listBillTypes();

      if (response?.success && Array.isArray(response.data?.bill_types)) {
        const fetchedItems = response.data.bill_types.map((billType) => ({
          id: billType.id,
          name: billType.name || "",
          isGstApplicable:
            billType.gst_applicable === 1 || billType.gst_applicable === "1",
          gstPercentage:
            billType.gst_percent !== undefined && billType.gst_percent !== null
              ? String(billType.gst_percent)
              : "0",
        }));

        fetchedItems.sort((a, b) => b.id - a.id);
        setBillTypes(fetchedItems);
      } else {
        console.error("Unexpected data format:", response);
        toast.error(response?.message || "Invalid data format received");
      }
    } catch (error) {
      console.error("Error fetching bill types:", error);
      toast.error(
        error.response?.data?.message || "Failed to fetch bill types",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const updateBillType = async (updatedBillType) => {
    try {
      const response = await updateBillTypeStatus({
        id: updatedBillType.id,
        name: updatedBillType.name,
        gst_applicable: updatedBillType.isGstApplicable ? 1 : 0,
        gst_percent: parseFloat(updatedBillType.gstPercentage) || 0,
        status: 1,
      });

      if (response?.success) {
        toast.success("Bill type updated successfully");
        const data = response.data?.bill_type || response.data || {};
        const next = {
          id: data.id ?? updatedBillType.id,
          name: data.name ?? updatedBillType.name ?? "",
          isGstApplicable:
            (data.gst_applicable ??
              (updatedBillType.isGstApplicable ? 1 : 0)) === 1,
          gstPercentage:
            data.gst_percent !== undefined && data.gst_percent !== null
              ? String(data.gst_percent)
              : String(parseFloat(updatedBillType.gstPercentage) || 0),
        };
        setBillTypes((prev) =>
          prev
            .map((bt) => (bt.id === next.id ? { ...bt, ...next } : bt))
            .sort((a, b) => b.id - a.id),
        );
      }
    } catch (error) {
      console.error("Error updating bill type:", error);
      toast.error(
        error.response?.data?.message || "Failed to update bill type",
      );
    }
  };

  useEffect(() => {
    fetchBillTypes();
  }, []);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in BillTypeTable, refetching bill types...",
        event.detail,
      );
      fetchBillTypes();
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log("BillTypeTable: Connected to socket server", newSocket.id);
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("BillTypeTable: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("BillTypeTable: Socket connection error:", error);
    });

    newSocket.on("bill_type_updated", (data) => {
      if (!data?.bill_type) return;
      const bt = data.bill_type;
      setBillTypes((prev) =>
        prev.map((billType) =>
          billType.id === bt.id
            ? {
                ...billType,
                name: bt.name || billType.name,
                isGstApplicable:
                  bt.gst_applicable === 1 || bt.gst_applicable === "1",
                gstPercentage:
                  bt.gst_percent !== undefined && bt.gst_percent !== null
                    ? String(bt.gst_percent)
                    : billType.gstPercentage,
              }
            : billType,
        ),
      );
    });

    newSocket.on("bill_type_created", (data) => {
      if (!data?.bill_type) return;
      const bt = data.bill_type;
      const newBillType = {
        id: bt.id,
        name: bt.name || "",
        isGstApplicable: bt.gst_applicable === 1 || bt.gst_applicable === "1",
        gstPercentage:
          bt.gst_percent !== undefined && bt.gst_percent !== null
            ? String(bt.gst_percent)
            : "0",
      };

      setBillTypes((prev) => {
        const exists = prev.some((billType) => billType.id === newBillType.id);
        if (exists) {
          return prev.map((billType) =>
            billType.id === newBillType.id ? newBillType : billType,
          );
        }
        return [newBillType, ...prev].sort((a, b) => b.id - a.id);
      });
    });

    return () => {
      newSocket.off("bill_type_updated");
      newSocket.off("bill_type_created");
      newSocket.disconnect();
    };
  }, []);

  const handleEditClick = (billType) => {
    setSelectedBillType({ ...billType });
    setShowEditModal(true);
  };

  const handleSaveChanges = () => {
    updateBillType(selectedBillType);
    setShowEditModal(false);
  };

  // Handle numeric input for GST percentage
  const handleGstPercentageChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setGstPercentage(value);
    }
  };

  // Handle Add Bill Type Form Submission
  const handleAddSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await createBillType({
        name: billTypeName,
        gst_applicable: isGstApplicable === "yes" ? 1 : 0,
        gst_percent:
          isGstApplicable === "yes" ? parseFloat(gstPercentage) || 0 : 0,
      });

      if (!response?.success) {
        throw new Error(response?.message || "Failed to add bill type");
      }

      toast.success(response.message || "Bill type added successfully");
      setShowAddModal(false);
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 2000);

      // Reset form fields
      setBillTypeName("");
      setIsGstApplicable("no");
      setGstPercentage("");

      // Update list from API response (or form values as fallback).
      const data = response.data?.bill_type || response.data || {};
      const next = {
        id: data.id,
        name: data.name ?? billTypeName ?? "",
        isGstApplicable:
          (data.gst_applicable ?? (isGstApplicable === "yes" ? 1 : 0)) === 1,
        gstPercentage:
          data.gst_percent !== undefined && data.gst_percent !== null
            ? String(data.gst_percent)
            : String(
                isGstApplicable === "yes" ? parseFloat(gstPercentage) || 0 : 0,
              ),
      };
      if (next.id !== undefined && next.id !== null) {
        setBillTypes((prev) => {
          const exists = prev.some((bt) => bt.id === next.id);
          const merged = exists
            ? prev.map((bt) => (bt.id === next.id ? { ...bt, ...next } : bt))
            : [next, ...prev];
          return merged.sort((a, b) => b.id - a.id);
        });
      } else {
        // If backend doesn't return the new id, fall back to a list refresh.
        fetchBillTypes();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(error.response?.data?.message || "Failed to add bill type");
    }
  };

  // Reset add form fields when modal closes
  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setBillTypeName("");
    setIsGstApplicable("no");
    setGstPercentage("");
  };

  // Filtering based on search
  const filteredBillTypes = billTypes.filter((billType) => {
    const name = billType.name || "";
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredBillTypes.length / itemsPerPage);
  const displayedBillTypes = filteredBillTypes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const handleItemsPerPageChange = (newSize) => {
    setItemsPerPage(newSize);
  };

  useEffect(() => {
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
    } else if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Bill Type Table</h4>
            <button
              className={`btn btn-success ${buttonClass}`}
              onClick={() => setShowAddModal(true)}
            >
              Add Bill Type
            </button>
          </div>

          <div className="card-body p-4">
            {/* Search Section */}
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Table */}
            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass}`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Bill Type Name</th>
                    <th>GST Applicable</th>
                    <th>GST Percentage</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="4" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : displayedBillTypes.length > 0 ? (
                    displayedBillTypes.map((billType) => (
                      <tr key={billType.id}>
                        <td>{billType.name || "N/A"}</td>
                        <td>
                          <span
                            className={`badge ${
                              billType.isGstApplicable
                                ? "bg-success-subtle text-success"
                                : "bg-danger-subtle text-danger"
                            }`}
                          >
                            {billType.isGstApplicable ? "Yes" : "No"}
                          </span>
                        </td>
                        <td>{billType.gstPercentage}%</td>
                        <td>
                          <button
                            className={`btn btn-primary btn-sm ${buttonClass}`}
                            onClick={() => handleEditClick(billType)}
                          >
                            <Icon
                              icon="solar:pen-bold"
                              width="16"
                              height="16"
                            />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        No bill types found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={filteredBillTypes.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>

      {/* Edit Bill Type Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Bill Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBillType && (
            <Form>
              <div className="row">
                <div className="mb-3 col-md-12">
                  <Form.Label>Bill Type Name</Form.Label>
                  <Form.Control
                    type="text"
                    className={buttonClass}
                    value={selectedBillType.name}
                    onChange={(e) =>
                      setSelectedBillType({
                        ...selectedBillType,
                        name: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="row">
                <div className="mb-3 col-md-12">
                  <Form.Label>Is GST Applicable?</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      label="Yes"
                      name="isGstApplicable"
                      checked={selectedBillType.isGstApplicable}
                      onChange={(e) =>
                        setSelectedBillType({
                          ...selectedBillType,
                          isGstApplicable: true,
                        })
                      }
                    />
                    <Form.Check
                      type="radio"
                      label="No"
                      name="isGstApplicable"
                      checked={!selectedBillType.isGstApplicable}
                      onChange={(e) =>
                        setSelectedBillType({
                          ...selectedBillType,
                          isGstApplicable: false,
                          gstPercentage: "0",
                        })
                      }
                    />
                  </div>
                </div>
              </div>
              {selectedBillType.isGstApplicable && (
                <div className="row">
                  <div className="mb-3 col-md-12">
                    <Form.Label>GST Percentage</Form.Label>
                    <Form.Control
                      type="text"
                      className={buttonClass}
                      value={selectedBillType.gstPercentage}
                      onChange={(e) =>
                        setSelectedBillType({
                          ...selectedBillType,
                          gstPercentage: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleSaveChanges}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Bill Type Modal */}
      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Bill Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form noValidate="" onSubmit={handleAddSubmit}>
            <div className="row">
              <div className="mb-3 col-md-12">
                <label className="form-label">Bill Type Name</label>
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  required
                  placeholder="Enter Bill Type Name"
                  value={billTypeName}
                  onChange={(e) => setBillTypeName(e.target.value)}
                />
              </div>
            </div>
            <div className="row">
              <div className="mb-3 col-md-12">
                <label className="form-label">Is GST Applicable?</label>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      type="radio"
                      name="isGstApplicable"
                      className="form-check-input"
                      value="yes"
                      checked={isGstApplicable === "yes"}
                      onChange={(e) => setIsGstApplicable(e.target.value)}
                    />
                    <label className="form-check-label">Yes</label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      name="isGstApplicable"
                      className="form-check-input"
                      value="no"
                      checked={isGstApplicable === "no"}
                      onChange={(e) => setIsGstApplicable(e.target.value)}
                    />
                    <label className="form-check-label">No</label>
                  </div>
                </div>
              </div>
            </div>
            {isGstApplicable === "yes" && (
              <div className="row">
                <div className="mb-3 col-md-12">
                  <label className="form-label">GST Percentage</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter GST %"
                    value={gstPercentage}
                    onChange={handleGstPercentageChange}
                  />
                </div>
              </div>
            )}
            <div className="d-flex flex-wrap gap-3 justify-content-end">
              <button
                type="submit"
                className={`btn btn-primary ${buttonClass}`}
              >
                Submit
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default BillTypeTable;
